import { tg } from "./webapp";

/**
 * Haptics are what separate a Mini App from a website. Every call is guarded:
 * older clients throw on an unknown method rather than ignoring it.
 */
function guard(run: () => void): void {
  try {
    run();
  } catch {
    /* client too old */
  }
}

/**
 * Two taps inside this window are one gesture, not two.
 *
 * Dragging a finger along a chip rail or a picker list fires a selection per
 * item crossed, which arrives as a continuous buzz rather than as a series of
 * ticks. Collapsing the repeats keeps the feedback legible as *counting*.
 */
const REPEAT_MS = 40;
let last = 0;

function pulse(run: () => void): void {
  const now = Date.now();
  if (now - last < REPEAT_MS) return;
  last = now;
  guard(run);
}

export const haptic = {
  /** Moving between options: chips, tabs, segmented controls, pickers. */
  select: () => pulse(() => tg?.HapticFeedback.selectionChanged()),
  /** A committed tap: opening a record, toggling, pressing a button. */
  tap: () => pulse(() => tg?.HapticFeedback.impactOccurred("light")),
  /** A heavier commitment: opening a sheet, promoting media. */
  press: () => pulse(() => tg?.HapticFeedback.impactOccurred("medium")),
  /**
   * The weight reserved for a step that cannot be taken back by tapping again.
   * Deleting a record is the only thing in the app that earns it — a confirm
   * that feels like its cancel is a confirm nobody reads.
   */
  commit: () => pulse(() => tg?.HapticFeedback.impactOccurred("heavy")),
  success: () => guard(() => tg?.HapticFeedback.notificationOccurred("success")),
  warning: () => guard(() => tg?.HapticFeedback.notificationOccurred("warning")),
  error: () => guard(() => tg?.HapticFeedback.notificationOccurred("error")),
};
